import { useModuleBEM } from "@b2bportal/core-themes";
import { ProductType } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import styles from "./RefundBreakdown.module.scss";

const ComponentName = "RefundBreakdown";

type HotelBreakdown = {
  roomLabel: string;
  subtotal: string;
  taxesAndFees?: string;
  pointsPaidLabel?: string;
  pointsPaid?: string;
  estimatedRefund: string;
};

type CarBreakdown = {
  totalPaidLabel: string;
  totalPaid: string;
  pointsPaidLabel?: string;
  pointsPaid?: string;
  estimatedRefund: string;
};

type FlightOrActivityBreakdown = {
  items: Array<{ label: string; value: string }>;
};

export type RefundBreakdownData =
  | { productType: ProductType.LODGING; data: HotelBreakdown }
  | { productType: ProductType.CARS; data: CarBreakdown }
  | { productType: ProductType.FLIGHTS; data: FlightOrActivityBreakdown }
  | { productType: ProductType.ACTIVITIES; data: FlightOrActivityBreakdown };

interface RefundBreakdownProps {
  breakdown: RefundBreakdownData;
  className?: string;
}

export const RefundBreakdown = ({
  breakdown,
  className,
}: RefundBreakdownProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, ComponentName);

  const renderBreakdownRows = () => {
    switch (breakdown.productType) {
      case ProductType.LODGING: {
        const data = breakdown.data;
        return (
          <>
            <div className={cn("row")}>
              <span>{data.roomLabel}</span>
              <span>{data.subtotal}</span>
            </div>
            {data.taxesAndFees != null && (
              <div className={cn("row")}>
                <span>{t("selfServe.taxesAndFees")}</span>
                <span>{data.taxesAndFees}</span>
              </div>
            )}
            {data.pointsPaidLabel != null && data.pointsPaid != null && (
              <div className={cn("row")}>
                <span>{data.pointsPaidLabel}</span>
                <span>{data.pointsPaid}</span>
              </div>
            )}
          </>
        );
      }
      case ProductType.CARS: {
        const data = breakdown.data;
        return (
          <>
            <div className={cn("row")}>
              <span>{data.totalPaidLabel}</span>
              <span>{data.totalPaid}</span>
            </div>
            {data.pointsPaidLabel != null && data.pointsPaid != null && (
              <div className={cn("row")}>
                <span>{data.pointsPaidLabel}</span>
                <span>{data.pointsPaid}</span>
              </div>
            )}
          </>
        );
      }
      case ProductType.FLIGHTS:
      case ProductType.ACTIVITIES: {
        const data = breakdown.data;
        return (
          <>
            {data.items.map((item) => (
              <div key={`${item.label}-${item.value}`} className={cn("row")}>
                <span>{item.label}</span>
                <span>{item.value}</span>
              </div>
            ))}
          </>
        );
      }
    }
  };

  const getEstimatedRefund = (): string => {
    switch (breakdown.productType) {
      case ProductType.LODGING:
        return breakdown.data.estimatedRefund;
      case ProductType.CARS:
        return breakdown.data.estimatedRefund;
      case ProductType.FLIGHTS:
      case ProductType.ACTIVITIES:
        return "";
    }
  };

  const estimatedRefund = getEstimatedRefund();

  return (
    <div className={clsx(block, className)}>
      <h3 className={cn("title")}>{t("selfServe.cancellationInformation")}</h3>
      <div className={cn("rows")}>{renderBreakdownRows()}</div>
      {estimatedRefund && (
        <>
          <div className={cn("divider")} />
          <div className={cn("refund-row")}>
            <h3 className={cn("refund-title")}>
              {t("selfServe.estimatedRefund")}
            </h3>
            <span className={cn("refund-value")}>{estimatedRefund}</span>
          </div>
        </>
      )}
    </div>
  );
};
